import { useState } from 'react';
import { AxiosError } from 'axios';
import type { AdminUserListItem, ApiResponse, UserRole } from '@/types/api';
import { Dialog } from '@/components/ui/dialog';
import {
  ConflictError,
  createUser,
  type CreatedUserResponse,
  type CreateUserPayload,
} from '../api/admin-users-api';

type Phase = 'form' | 'submitting' | 'reveal';

interface FieldErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}

interface AddUserDialogProps {
  open: boolean;
  /** Called once the admin confirms they've saved the password. Triggers list refetch. */
  onCreated: (user: AdminUserListItem) => void;
  /** Called when the user dismisses the form (Esc / backdrop / cancel). Not called from the reveal panel. */
  onClose: () => void;
}

const ROLE_OPTIONS: UserRole[] = ['User', 'Admin'];

function parseServerErrors(body: ApiResponse<unknown> | undefined): {
  fields: FieldErrors;
  banner?: string;
} {
  if (!body) return { fields: {}, banner: undefined };

  // Backend returns `errors: string[]` and/or `message: string`. We can't
  // reliably attribute each string to a field server-side, so we route
  // obvious matches (containing "email"/"first name"/etc.) into fields and
  // fall back to a banner.
  const fields: FieldErrors = {};
  let banner: string | undefined;

  const lines = body.errors && body.errors.length > 0 ? body.errors : body.message ? [body.message] : [];
  for (const line of lines) {
    const lower = line.toLowerCase();
    if (lower.includes('email')) fields.email = fields.email ?? line;
    else if (lower.includes('first')) fields.firstName = fields.firstName ?? line;
    else if (lower.includes('last')) fields.lastName = fields.lastName ?? line;
    else if (lower.includes('role')) fields.role = fields.role ?? line;
    else banner = banner ?? line;
  }
  return { fields, banner };
}

function toListItem(user: CreatedUserResponse): AdminUserListItem {
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    role: user.role,
    isActive: true,
    createdAt: new Date().toISOString(),
    fullName: `${user.firstName} ${user.lastName}`.trim(),
    // Newly-created users always start with the forced-change flag set
    // server-side; mirror that here so the table row carries the truth.
    mustChangePassword: true,
  };
}

export function AddUserDialog({ open, onCreated, onClose }: AddUserDialogProps) {
  const [phase, setPhase] = useState<Phase>('form');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>('User');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [createdUser, setCreatedUser] = useState<CreatedUserResponse | null>(null);

  const resetState = () => {
    setPhase('form');
    setEmail('');
    setFirstName('');
    setLastName('');
    setRole('User');
    setFieldErrors({});
    setBannerError(null);
    setCreatedUser(null);
  };

  const handleClose = () => {
    if (phase === 'submitting') return; // ignore close while in-flight
    resetState();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldErrors({});
    setBannerError(null);

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    // Client-side required-field validation (mirrors server `[Required]`).
    const localErrors: FieldErrors = {};
    if (!trimmedEmail) localErrors.email = 'Email is required.';
    if (!trimmedFirst) localErrors.firstName = 'First name is required.';
    if (!trimmedLast) localErrors.lastName = 'Last name is required.';
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }

    const payload: CreateUserPayload = {
      email: trimmedEmail,
      firstName: trimmedFirst,
      lastName: trimmedLast,
      role,
    };

    setPhase('submitting');
    try {
      const result = await createUser(payload);
      setCreatedUser(result);
      setPhase('reveal');
    } catch (err) {
      if (err instanceof ConflictError) {
        setFieldErrors({ email: err.message });
        setPhase('form');
        return;
      }
      if (err instanceof AxiosError && err.response?.status === 400) {
        const parsed = parseServerErrors(err.response.data as ApiResponse<unknown> | undefined);
        setFieldErrors(parsed.fields);
        setBannerError(parsed.banner ?? null);
        setPhase('form');
        return;
      }
      setBannerError('Could not create user. Check API connectivity and try again.');
      setPhase('form');
    }
  };

  const handleRevealConfirm = () => {
    if (!createdUser) return;
    const created = toListItem(createdUser);
    resetState();
    onCreated(created);
    onClose();
  };

  // Reveal panel uses a non-dismissible Dialog (no Esc/backdrop close).
  if (phase === 'reveal' && createdUser) {
    return (
      <Dialog open={open} onClose={handleClose} title="User created" dismissible={false}>
        <PasswordRevealPanel user={createdUser} onConfirm={handleRevealConfirm} />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} title="Add user" dismissible={phase !== 'submitting'}>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-user-form" noValidate>
        {bannerError && (
          <div
            role="alert"
            data-testid="add-user-banner-error"
            className="rounded border border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300"
          >
            {bannerError}
          </div>
        )}

        <Field
          id="add-user-email"
          label="Email"
          error={fieldErrors.email}
          input={
            <input
              id="add-user-email"
              data-testid="add-user-email-input"
              type="email"
              autoComplete="off"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
            />
          }
        />
        <Field
          id="add-user-first-name"
          label="First name"
          error={fieldErrors.firstName}
          input={
            <input
              id="add-user-first-name"
              data-testid="add-user-first-name-input"
              type="text"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
            />
          }
        />
        <Field
          id="add-user-last-name"
          label="Last name"
          error={fieldErrors.lastName}
          input={
            <input
              id="add-user-last-name"
              data-testid="add-user-last-name-input"
              type="text"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
            />
          }
        />
        <Field
          id="add-user-role"
          label="Role"
          error={fieldErrors.role}
          input={
            <select
              id="add-user-role"
              data-testid="add-user-role-select"
              value={role}
              onChange={(e) => setRole(e.target.value as UserRole)}
              className="w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          }
        />

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid="add-user-cancel-button"
            onClick={handleClose}
            disabled={phase === 'submitting'}
            className="rounded border border-gray-600 px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="add-user-submit-button"
            disabled={phase === 'submitting'}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {phase === 'submitting' ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

interface FieldProps {
  id: string;
  label: string;
  error?: string;
  input: React.ReactNode;
}

function Field({ id, label, error, input }: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-200">
        {label}
      </label>
      {input}
      {error && (
        <p
          data-testid={`${id}-error`}
          role="alert"
          className="mt-1 text-xs text-red-400"
        >
          {error}
        </p>
      )}
    </div>
  );
}

interface PasswordRevealPanelProps {
  user: CreatedUserResponse;
  onConfirm: () => void;
}

function PasswordRevealPanel({ user, onConfirm }: PasswordRevealPanelProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = user.initialPassword;
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch {
      // fall through to execCommand fallback
    }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="space-y-4" data-testid="add-user-reveal-panel">
      <p className="text-sm text-gray-300">
        Share this password with <span className="font-semibold text-white">{user.email}</span> via a
        secure channel. It will not be shown again.
      </p>

      <div>
        <label htmlFor="reveal-password-input" className="mb-1 block text-sm font-medium text-gray-200">
          Initial password
        </label>
        <div className="flex gap-2">
          <input
            id="reveal-password-input"
            data-testid="reveal-password-input"
            type="text"
            readOnly
            value={user.initialPassword}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 font-mono text-sm text-gray-100"
          />
          <button
            type="button"
            data-testid="reveal-copy-button"
            onClick={handleCopy}
            className={`rounded px-3 py-2 text-sm font-medium text-white transition-colors ${
              copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
            }`}
            aria-live="polite"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          data-testid="reveal-close-button"
          onClick={onConfirm}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
        >
          I&apos;ve saved it, close
        </button>
      </div>
    </div>
  );
}
